import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Alert from "@mui/material/Alert";
import { LojaPageContainer } from "../../common";
import { detalharPedidoLoja } from "services/lojaPedidos";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import type { TipoEntrega } from "interfaces/loja/pedidoOperacional";
import { copyRetiradaUi } from "interfaces/loja/pedidoRetirada";
import { useMaterialUIController, setMessage } from "context";
import { extractPedidoDetalheResponse } from "utils/pedidoApiResponse";
import { labelTipoEntrega } from "utils/pedidoOperacional";
import PedidoRetiradaOperacional from "./components/PedidoRetiradaOperacional";

/**
 * Fase 5 — Retirada (loja ou evento).
 * Rota: /loja/pedidos/:pedidoId/retirada
 */
export default function LojaPedidoRetirada() {
  const { pedidoId } = useParams<{ pedidoId: string }>();
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();

  const [detalhe, setDetalhe] = useState<IPedidoDetalhe | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reloadDetalhe = useCallback(async () => {
    if (!pedidoId) return;
    const { data, status } = await detalharPedidoLoja(pedidoId);
    if (status === 200) {
      const p = extractPedidoDetalheResponse(data);
      if (p?.id) setDetalhe(p);
    }
  }, [pedidoId]);

  useEffect(() => {
    (async () => {
      setLoadError(null);
      if (!pedidoId) {
        setLoadError("Pedido não informado.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, status } = await detalharPedidoLoja(pedidoId);
        if (status === 200) {
          const p = extractPedidoDetalheResponse(data);
          if (p?.id) {
            setDetalhe(p);
          } else {
            setDetalhe(null);
            setLoadError("Resposta inválida ao carregar o pedido.");
          }
        } else {
          setDetalhe(null);
          setLoadError("Não foi possível carregar o pedido.");
        }
      } catch (e: unknown) {
        const text =
          e instanceof Error ? e.message : "Falha ao carregar o pedido.";
        setLoadError(text);
        setMessage(dispatch, { open: true, severity: "error", text });
      } finally {
        setLoading(false);
      }
    })();
  }, [pedidoId, dispatch]);

  const handleAfterAction = useCallback(
    async (patch?: Partial<IPedidoDetalhe>) => {
      await reloadDetalhe();
      if (patch) {
        setDetalhe((d) => (d ? { ...d, ...patch } : d));
      }
    },
    [reloadDetalhe]
  );

  const voltar = () => {
    if (pedidoId) navigate(`/loja/pedidos/${pedidoId}`);
    else navigate(-1);
  };

  const tipo = detalhe?.tipoEntrega;
  const ehRetirada =
    tipo === "RETIRADA_LOJA" || tipo === "RETIRADA_EVENTO";
  const copyUi = copyRetiradaUi(tipo);

  return (
    <LojaPageContainer
      title="Retirada"
      subtitle={
        detalhe?.numero
          ? `Pedido ${detalhe.numero} · ${copyUi.pageSubtitle}`
          : pedidoId
            ? `Pedido ${pedidoId.slice(0, 8)}…`
            : undefined
      }
    >
      <MDBox mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <MDButton variant="outlined" color="dark" onClick={voltar}>
          Voltar ao pedido
        </MDButton>
      </MDBox>

      {loading ? (
        <MDTypography variant="button">Carregando…</MDTypography>
      ) : loadError ? (
        <MDTypography color="error" variant="button">
          {loadError}
        </MDTypography>
      ) : !pedidoId || !detalhe ? (
        <MDTypography variant="button">Pedido não encontrado.</MDTypography>
      ) : !ehRetirada ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Esta etapa é para pedidos com{" "}
          <strong>retirada na loja</strong> ou{" "}
          <strong>entrega no evento</strong>. Este pedido está como{" "}
          {labelTipoEntrega((tipo ?? "ENTREGA") as TipoEntrega)}. Use a expedição
          quando for envio.
        </Alert>
      ) : (
        <PedidoRetiradaOperacional
          pedidoId={pedidoId}
          detalhe={detalhe}
          tipoEntrega={tipo as TipoEntrega}
          onAfterAction={handleAfterAction}
        />
      )}
    </LojaPageContainer>
  );
}
